/**
 * Cliente HTTP tipado contra el backend de Tivy v2.
 *
 * - Lee la base URL de `VITE_API_URL` (ver `.env.example`).
 * - Inyecta `Authorization: Bearer <token>` si hay sesión en `useAuthStore`.
 * - Parsea errores del backend con shape `{ error: { code, message, details } }`
 *   y los lanza como `ApiError`.
 * - En `401`, hace logout y deja que el router superior redirija a /login.
 *
 * Ver ADR 004 para el rationale de no usar axios/ky/react-query.
 */

import { useAuthStore } from '../stores/authStore'
import type { ApiErrorBody, ApiErrorCode } from '../types/api'

/**
 * Base URL del backend. En dev/preview es `''` (paths relativos +
 * proxy de Vite — F016 §4.1 fix). En producción se setea al origin del
 * backend expuesto al público. Acepta vacío como valor válido (no
 * trigger del fallback `??`), por eso usamos `??` y NO `||`.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly details: Record<string, unknown> | undefined

  constructor(opts: {
    code: ApiErrorCode
    message: string
    status: number
    details?: Record<string, unknown> | undefined
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.code = opts.code
    this.status = opts.status
    this.details = opts.details
  }

  isValidation(): boolean {
    return this.code === 'validation_error'
  }

  fieldError(field: string): string | undefined {
    if (!this.details) return undefined
    const d = this.details
    if (d['field'] === field && typeof d['reason'] === 'string') {
      return this.message
    }
    const fields = d['fields']
    if (fields && typeof fields === 'object' && field in fields) {
      const v = (fields as Record<string, unknown>)[field]
      if (typeof v === 'string') return v
    }
    return undefined
  }
}

export interface HealthResponse {
  status: 'ok' | string
  [key: string]: unknown
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const init: RequestInit = signal ? { signal } : {}
  const res = await fetch(`${API_BASE}/health`, init)
  if (!res.ok) {
    throw new Error(`Health check falló: HTTP ${res.status}`)
  }
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return (await res.json()) as HealthResponse
  }
  const text = await res.text()
  return { status: text.trim() || 'ok' }
}

export function getApiBaseUrl(): string {
  return API_BASE
}

interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** Si true, no corre el auto-logout en 401. Útil para el propio /auth/login. */
  skipAuthHandling?: boolean
  /**
   * Token opaco del dispositivo (F004 §4.4). Si se pasa, se manda como
   * `X-Device-Token` en lugar del `Authorization: Bearer` del auth store.
   * Pensado para heartbeat y para cualquier request que haga el device por su
   * cuenta sin tener un usuario logueado.
   */
  deviceToken?: string
  /**
   * Token de invitación del invitado (F007 §4.5). Se manda como
   * `X-Invitation-Token`. Mutuamente excluyente con `deviceToken` y con el
   * Bearer JWT — el primero presente en orden `deviceToken > invitationToken
   * > JWT` es el que viaja. Pensado para que la PWA del invitado consuma
   * endpoints existentes (uploads de photobooth, scores) sin user logueado.
   */
  invitationToken?: string
  /**
   * Setea `cache: 'no-store'` en el fetch — el browser bypass-ea su HTTP
   * cache (no lee, no escribe). Defensa en profundidad para endpoints
   * públicos del invitado: el backend ya manda `Cache-Control: no-cache,
   * no-store, must-revalidate`, pero invitados que ya tenían respuestas
   * cacheadas pre-fix igual seguirían viéndolas hasta que expiren por
   * heurística del browser. Con `noCache: true` la próxima carga va a
   * ignorar lo cacheado y volver a network.
   *
   * Solo aplica a endpoints donde el backend devuelve datos frescos por
   * decisión (byToken, public-info, public-scores). Para cosas estáticas
   * como assets/icons, dejar false (cacheo natural sigue siendo deseable).
   */
  noCache?: boolean
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query)
  const headers: Record<string, string> = {
    Accept: 'application/json'
  }
  if (opts.deviceToken) {
    headers['X-Device-Token'] = opts.deviceToken
  } else if (opts.invitationToken) {
    headers['X-Invitation-Token'] = opts.invitationToken
  } else {
    const token = useAuthStore.getState().token
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers
  }
  if (opts.signal) init.signal = opts.signal
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(opts.body)
  }
  if (opts.noCache) {
    // `no-store`: el browser ni lee ni escribe su HTTP cache para esta
    // request. Combinado con el `Cache-Control` server-side, garantiza
    // que invitados que ya tenían respuestas cacheadas pre-fix dejen de
    // verlas tan pronto como recargan.
    init.cache = 'no-store'
  }

  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    throw new ApiError({
      code: 'internal',
      message: err instanceof Error ? err.message : 'Error de red',
      status: 0
    })
  }

  if (res.status === 204) return undefined as T

  const contentType = res.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload: unknown = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    const apiErr = asApiError(payload, res.status)
    if (res.status === 401 && !opts.skipAuthHandling) {
      useAuthStore.getState().logout()
    }
    throw apiErr
  }

  return payload as T
}

function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  const base = path.startsWith('http') ? path : `${API_BASE}${path}`
  if (!query) return base
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) continue
    usp.set(k, String(v))
  }
  const qs = usp.toString()
  return qs ? `${base}?${qs}` : base
}

export interface ApiUploadOptions {
  method?: 'POST' | 'PUT'
  /** Reporta progreso del upload entre 0 y 1. No se llama si el tamaño es desconocido. */
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
  /**
   * F012 §4.3: el slot photobooth sube fotos con `X-Device-Token`, no JWT.
   * Si se pasa, prevalece sobre el `Authorization` del auth store.
   */
  deviceToken?: string
  /**
   * F007 §4.5: la PWA del invitado sube fotos al photobooth mobile usando su
   * `X-Invitation-Token`. Mutuamente excluyente con `deviceToken` y JWT.
   */
  invitationToken?: string
}

/**
 * Sube FormData (típicamente con un campo `file`) y parsea la respuesta JSON
 * como `T`. Usa XHR en vez de fetch porque fetch no expone progreso de upload.
 */
export function apiUpload<T>(
  path: string,
  formData: FormData,
  opts: ApiUploadOptions = {}
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`
  const token = useAuthStore.getState().token

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(opts.method ?? 'POST', url)
    xhr.responseType = 'text'
    xhr.setRequestHeader('Accept', 'application/json')
    if (opts.deviceToken) {
      xhr.setRequestHeader('X-Device-Token', opts.deviceToken)
    } else if (opts.invitationToken) {
      xhr.setRequestHeader('X-Invitation-Token', opts.invitationToken)
    } else if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          opts.onProgress!(e.loaded / e.total)
        }
      }
    }

    xhr.onload = () => {
      const status = xhr.status
      if (status === 204) {
        resolve(undefined as T)
        return
      }
      let payload: unknown
      const contentType = xhr.getResponseHeader('content-type') ?? ''
      if (contentType.includes('application/json')) {
        try {
          payload = JSON.parse(xhr.responseText)
        } catch {
          payload = xhr.responseText
        }
      } else {
        payload = xhr.responseText
      }
      if (status >= 200 && status < 300) {
        resolve(payload as T)
        return
      }
      const err = asApiError(payload, status)
      if (status === 401) {
        useAuthStore.getState().logout()
      }
      reject(err)
    }

    xhr.onerror = () => {
      reject(new ApiError({ code: 'internal', message: 'Error de red', status: 0 }))
    }

    xhr.onabort = () => {
      reject(new ApiError({ code: 'internal', message: 'Upload cancelado', status: 0 }))
    }

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort()
        return
      }
      opts.signal.addEventListener('abort', () => xhr.abort())
    }

    xhr.send(formData)
  })
}

function asApiError(payload: unknown, status: number): ApiError {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as ApiErrorBody).error
  ) {
    const { code, message, details } = (payload as ApiErrorBody).error
    return new ApiError(
      details === undefined
        ? { code, message, status }
        : { code, message, status, details }
    )
  }
  const fallback = typeof payload === 'string' ? payload : `HTTP ${status}`
  // Iter 2026-05-16 audit: 410 default era `device_token_invalid` —
  // pero 410 Gone es genérico (invitación expirada, gallery borrada,
  // device unpaired, etc.). Backend normalmente devuelve el code
  // específico en el payload; este fallback solo aplica cuando el
  // payload está malformado (sin `error.code`). Defaultear a
  // 'device_token_invalid' provocaba que el deviceStore se
  // auto-limpiara en respuestas malformadas no-device. Ahora
  // usamos 'gone' genérico — los consumidores que necesitan tratar
  // device_token_invalid igual lo reciben cuando el backend lo
  // emite explícitamente.
  return new ApiError({
    code:
      status === 404
        ? 'not_found'
        : status === 401
          ? 'unauthorized'
          : status === 410
            ? 'gone'
            : 'internal',
    message: fallback,
    status
  })
}
